import React, { useEffect, useState, useRef } from "react";
import {
  Sparkles, Send, Loader2, Mic, CheckCircle2, AlertCircle, RotateCw,
  Brain, Wrench, Eye, Hexagon, Zap, Calendar, Clock as ClockIcon,
  PlayCircle, Trash2, Power, Plus, X
} from "lucide-react";
import { api } from "../api";

const TOOL_LABELS = {
  create_task: { label: "Create Task", color: "#34d399" },
  create_note: { label: "Create Note", color: "#3b82f6" },
  create_event: { label: "Schedule Event", color: "#a78bfa" },
  summarize_unread_mail: { label: "Summarize Inbox", color: "#63a0ff" },
  summarize_today_meetings: { label: "Today's Meetings", color: "#a78bfa" },
  queue_email_reply: { label: "Draft Email Reply", color: "#a78bfa" },
  queue_social_post: { label: "Draft Social Post", color: "#f472b6" },
  queue_dm_reply: { label: "Draft DM Reply", color: "#f472b6" },
  list_pending_approvals: { label: "Check Approvals", color: "#3b82f6" },
  team_attendance_today: { label: "Team Attendance", color: "#8b5cf6" },
  finish: { label: "Finish", color: "#34d399" },
};

const SAMPLE_GOALS = [
  "Prep my morning: summarize unread emails, list today's meetings, and create a task to review the most important email.",
  "Draft a Twitter post about HEXA OS launching Novo, then add a note explaining why we built it.",
  "Reply to the most recent unread email in my inbox.",
  "Create 3 tasks for this week's launch: write changelog, post on LinkedIn, send investor update.",
];

function StepRow({ step, idx, last }) {
  const tool = TOOL_LABELS[step.action] || { label: step.action, color: "#c7c1ff" };
  const ok = step.observation?.ok !== false && !step.error;
  const obs = step.observation || {};
  const obsKey =
    obs.summary ||
    obs.title ||
    obs.subject ||
    (obs.count != null ? `${obs.count} items` : "") ||
    obs.error ||
    "";
  return (
    <div className="relative flex gap-3" data-testid={`wingman-step-${idx}`}>
      {/* vertical line */}
      {!last && <div className="absolute left-[15px] top-8 bottom-0 w-px bg-[#8b5cf6]/20" />}
      <span
        className="relative z-[1] flex items-center justify-center"
        style={{
          width: 32, height: 36,
          clipPath: "polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)",
          background: ok ? `${tool.color}22` : "rgba(248,113,113,0.18)",
          border: `1px solid ${ok ? tool.color : "#f87171"}66`,
          flexShrink: 0,
        }}
      >
        {step.action === "finish" ? (
          <CheckCircle2 size={14} color={tool.color} />
        ) : ok ? (
          <Wrench size={12} color={tool.color} />
        ) : (
          <AlertCircle size={12} color="#f87171" />
        )}
      </span>
      <div className="flex-1 pb-3">
        <div className="flex items-center gap-2 mb-1">
          <span className="mono text-[10px] uppercase tracking-widest" style={{ color: tool.color }}>
            {tool.label}
          </span>
          {step.args && Object.keys(step.args).length > 0 && step.action !== "finish" && (
            <span className="mono text-[9px] text-[#c7c1ff]/45 truncate max-w-[300px]">
              {Object.entries(step.args).map(([k, v]) => `${k}=${typeof v === "string" ? `"${v.slice(0, 30)}"` : v}`).join(" ")}
            </span>
          )}
        </div>
        {step.thought && (
          <div className="flex items-start gap-1.5 text-[11px] text-[#c7c1ff]/70 mb-1.5">
            <Brain size={10} className="mt-0.5 flex-shrink-0 text-[#a78bfa]" />
            <span className="italic">{step.thought}</span>
          </div>
        )}
        {step.action !== "finish" && obsKey && (
          <div className="flex items-start gap-1.5 text-[11px] text-[#e8f4f8]">
            <Eye size={10} className="mt-0.5 flex-shrink-0 text-[#a78bfa]" />
            <span className="whitespace-pre-wrap">{obsKey}</span>
          </div>
        )}
      </div>
    </div>
  );
}

function RunCard({ run }) {
  const [expanded, setExpanded] = useState(true);
  return (
    <div
      className="panel p-4 space-y-3"
      style={{ clipPath: "polygon(0 0, calc(100% - 14px) 0, 100% 14px, 100% 100%, 14px 100%, 0 calc(100% - 14px))" }}
      data-testid={`wingman-run-${run.id}`}
    >
      <div className="flex items-start gap-2">
        <Zap size={14} color="#3b82f6" className="mt-0.5" />
        <div className="flex-1">
          <div className="mono text-[10px] uppercase tracking-widest text-[#3b82f6]">// goal</div>
          <div className="text-sm text-[#e8f4f8] mt-0.5">{run.goal}</div>
        </div>
        <span className="mono text-[9px] uppercase tracking-widest px-1.5 py-0.5"
          style={{
            background: run.status === "done" ? "rgba(52,211,153,0.18)" : run.status === "error" ? "rgba(248,113,113,0.18)" : "rgba(139,92,246,0.18)",
            color: run.status === "done" ? "#34d399" : run.status === "error" ? "#f87171" : "#8b5cf6",
            border: `1px solid ${run.status === "done" ? "#34d399" : run.status === "error" ? "#f87171" : "#8b5cf6"}55`,
          }}
        >{run.status}</span>
      </div>

      <button
        onClick={() => setExpanded(e => !e)}
        className="mono text-[10px] uppercase tracking-widest text-[#c7c1ff]/55 hover:text-[#8b5cf6]"
      >
        {expanded ? "▾" : "▸"} {run.steps?.length || 0} steps
      </button>

      {expanded && (
        <div className="space-y-0 pl-1">
          {(run.steps || []).map((s, i) => (
            <StepRow key={i} step={s} idx={i} last={i === run.steps.length - 1} />
          ))}
        </div>
      )}

      {run.summary && (
        <div className="bg-[#34d399]/10 border border-[#34d399]/30 p-3" data-testid={`wingman-summary-${run.id}`}>
          <div className="mono text-[10px] uppercase tracking-widest text-[#34d399] mb-1 flex items-center gap-1">
            <CheckCircle2 size={11} /> // novo summary
          </div>
          <div className="text-sm whitespace-pre-wrap text-[#e8f4f8]">{run.summary}</div>
        </div>
      )}
    </div>
  );
}

export default function Wingman({ initialGoal }) {
  const [tab, setTab] = useState("now");
  const [goal, setGoal] = useState(initialGoal || "");
  const [runs, setRuns] = useState([]);
  const [busy, setBusy] = useState(false);
  const [listening, setListening] = useState(false);
  const inputRef = useRef(null);

  const loadHistory = async () => {
    try {
      const r = await api.get("/wingman/runs");
      setRuns(r.data || []);
    } catch { /* ignore */ }
  };

  useEffect(() => { loadHistory(); }, []);
  useEffect(() => {
    if (initialGoal) { setGoal(initialGoal); setTab("now"); }
  }, [initialGoal]);

  const run = async (overrideGoal) => {
    const g = (overrideGoal || goal).trim();
    if (!g) return;
    setBusy(true);
    const optimistic = { id: `tmp-${Date.now()}`, goal: g, status: "running", steps: [], summary: "", _tmp: true };
    setRuns(prev => [optimistic, ...prev]);
    try {
      const res = await api.post("/wingman/run", { goal: g });
      setRuns(prev => [res.data, ...prev.filter(x => !x._tmp)]);
    } catch (e) {
      setRuns(prev => [{ ...optimistic, status: "error", summary: "Novo failed: " + (e?.response?.data?.detail || "unknown error") }, ...prev.filter(x => !x._tmp)]);
    }
    setBusy(false);
    setGoal("");
  };

  const startListening = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { alert("Voice not supported."); return; }
    const r = new SR();
    r.lang = "en-US"; r.continuous = false; r.interimResults = true;
    r.onstart = () => setListening(true);
    r.onend = () => setListening(false);
    r.onerror = () => setListening(false);
    r.onresult = (ev) => {
      let t = "";
      for (let i = ev.resultIndex; i < ev.results.length; i++) t += ev.results[i][0].transcript;
      setGoal(t);
      if (ev.results[ev.results.length - 1].isFinal) setTimeout(() => run(t), 200);
    };
    r.start();
  };

  return (
    <div className="h-full flex flex-col" style={{ background: "#ffffff" }}>
      <div className="border-b border-[#8b5cf6]/15 px-5 py-3 flex items-center gap-3">
        <span
          className="inline-block"
          style={{
            width: 18, height: 20,
            clipPath: "polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)",
            background: "linear-gradient(160deg, #3b82f6, #f59e0b)",
            boxShadow: "0 0 12px rgba(59,130,246,0.5)",
          }}
        />
        <div>
          <div className="mono text-sm tracking-widest text-[#3b82f6] font-bold">WINGMAN</div>
          <div className="mono text-[9px] uppercase tracking-[0.3em] text-[#c7c1ff]/50">// agentic ai · plans · acts · learns</div>
        </div>
        <div className="ml-6 flex items-center">
          <button
            onClick={() => setTab("now")}
            data-testid="wingman-tab-now"
            className={`px-4 py-2 mono text-[11px] uppercase tracking-widest flex items-center gap-1.5 ${tab === "now" ? "text-[#3b82f6]" : "text-[#c7c1ff]/60 hover:text-[#8b5cf6]"}`}
            style={{ borderBottom: tab === "now" ? "2px solid #3b82f6" : "2px solid transparent" }}
          >
            <Zap size={11} /> run now
          </button>
          <button
            onClick={() => setTab("schedules")}
            data-testid="wingman-tab-schedules"
            className={`px-4 py-2 mono text-[11px] uppercase tracking-widest flex items-center gap-1.5 ${tab === "schedules" ? "text-[#3b82f6]" : "text-[#c7c1ff]/60 hover:text-[#8b5cf6]"}`}
            style={{ borderBottom: tab === "schedules" ? "2px solid #3b82f6" : "2px solid transparent" }}
          >
            <Calendar size={11} /> scheduled
          </button>
        </div>
        <div className="ml-auto mono text-[9px] uppercase tracking-widest text-[#c7c1ff]/45">
          {runs.length} run{runs.length === 1 ? "" : "s"}
        </div>
      </div>

      {tab === "now" ? (
        <NowTab goal={goal} setGoal={setGoal} runs={runs} busy={busy} run={run} listening={listening} startListening={startListening} inputRef={inputRef} />
      ) : (
        <SchedulesTab onRunNow={() => { loadHistory(); }} />
      )}
    </div>
  );
}

function NowTab({ goal, setGoal, runs, busy, run, listening, startListening, inputRef }) {
  return (
    <>
      <div className="p-5 border-b border-[#8b5cf6]/10">
        <div className="panel p-3 flex items-center gap-2" style={{
          background: "linear-gradient(180deg, rgba(59,130,246,0.08), rgba(139,92,246,0.04))",
          clipPath: "polygon(0 0, calc(100% - 14px) 0, 100% 14px, 100% 100%, 14px 100%, 0 calc(100% - 14px))",
        }}>
          <input
            ref={inputRef}
            data-testid="wingman-goal-input"
            value={goal}
            onChange={(e) => setGoal(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") run(); }}
            placeholder="What should Novo do? e.g. 'prep my day' or 'draft a launch post and follow-up task'"
            className="flex-1 bg-transparent outline-none px-3 py-2 text-sm placeholder-[#c7c1ff]/40"
            autoFocus
          />
          <button
            data-testid="wingman-mic"
            onClick={startListening}
            className={`p-2.5 ${listening ? "text-[#3b82f6] pulse-amber" : "text-[#c7c1ff]/60 hover:text-[#8b5cf6]"}`}
          >
            <Mic size={16} />
          </button>
          <button
            data-testid="wingman-run"
            onClick={() => run()}
            disabled={busy || !goal.trim()}
            className="px-5 py-2.5 mono text-[11px] uppercase tracking-[0.3em] font-bold flex items-center gap-1.5 disabled:opacity-40"
            style={{ background: "linear-gradient(135deg, #3b82f6, #f59e0b)", color: "#0b1014", clipPath: "polygon(8px 0,100% 0,calc(100% - 8px) 100%,0 100%)" }}
          >
            {busy ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />}
            {busy ? "thinking…" : "deploy"}
          </button>
        </div>

        <div className="mt-3 flex flex-wrap gap-2">
          {SAMPLE_GOALS.map((s, i) => (
            <button
              key={i}
              data-testid={`wingman-sample-${i}`}
              onClick={() => { setGoal(s); inputRef.current?.focus(); }}
              disabled={busy}
              className="px-3 py-1.5 mono text-[10px] uppercase tracking-widest border border-[#8b5cf6]/20 hover:border-[#3b82f6]/50 hover:text-[#3b82f6] text-[#c7c1ff]/70 transition-colors"
              style={{ borderRadius: 999 }}
            >
              {s.slice(0, 56)}{s.length > 56 ? "…" : ""}
            </button>
          ))}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-5 space-y-4">
        {runs.length === 0 ? (
          <div className="text-center py-16">
            <Hexagon size={48} color="#3b82f6" className="mx-auto opacity-30 mb-3" />
            <div className="mono text-xs uppercase tracking-[0.3em] text-[#c7c1ff]/55">// no runs yet</div>
            <div className="mono text-[10px] text-[#c7c1ff]/40 mt-1">type a goal above · wingman will plan and act</div>
          </div>
        ) : (
          runs.map(r => <RunCard key={r.id} run={r} />)
        )}
      </div>
    </>
  );
}

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function SchedulesTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openNew, setOpenNew] = useState(false);

  const load = async () => {
    setLoading(true);
    try { setItems((await api.get("/wingman/schedules")).data || []); } catch { /* ignore */ }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, []);

  const toggle = async (s) => {
    await api.put(`/wingman/schedules/${s.id}`, { enabled: !s.enabled });
    load();
  };
  const remove = async (s) => {
    if (!window.confirm(`Delete schedule "${s.name}"?`)) return;
    await api.delete(`/wingman/schedules/${s.id}`);
    load();
  };
  const runNow = async (s) => {
    setItems(items.map(x => x.id === s.id ? { ...x, _runNowBusy: true } : x));
    try {
      await api.post(`/wingman/schedules/${s.id}/run-now`);
    } catch { /* ignore */ }
    load();
  };

  const fmtNext = (s) => {
    if (!s.next_run_at) return "—";
    const d = new Date(s.next_run_at);
    return d.toLocaleString([], { weekday: "short", hour: "2-digit", minute: "2-digit", month: "short", day: "numeric" });
  };
  const fmtLast = (s) => {
    if (!s.last_run_at) return "never";
    return new Date(s.last_run_at).toLocaleString([], { hour: "2-digit", minute: "2-digit", month: "short", day: "numeric" });
  };
  const cadenceLabel = (s) => {
    if (s.cadence === "interval") return `every ${s.interval_minutes}m`;
    if (s.cadence === "weekly") return `weekly · ${DAYS[s.day_of_week]} @ ${s.time}`;
    return `daily @ ${s.time}`;
  };

  return (
    <div className="flex-1 overflow-y-auto p-5 space-y-4">
      <div className="flex items-center gap-3">
        <Calendar size={14} color="#3b82f6" />
        <div className="mono text-[11px] uppercase tracking-[0.3em] text-[#3b82f6]">// scheduled novo runs · {items.filter(x => x.enabled).length}/{items.length} active</div>
        <button
          data-testid="schedule-new"
          onClick={() => setOpenNew(true)}
          className="ml-auto btn-accent text-[11px] uppercase tracking-widest mono flex items-center gap-1.5"
        >
          <Plus size={12} /> new schedule
        </button>
      </div>

      {loading && items.length === 0 ? (
        <div className="mono text-[10px] text-[#c7c1ff]/50 uppercase tracking-widest">loading…</div>
      ) : items.length === 0 ? (
        <div className="text-center py-16">
          <Calendar size={48} color="#3b82f6" className="mx-auto opacity-30 mb-3" />
          <div className="mono text-xs uppercase tracking-[0.3em] text-[#c7c1ff]/55">// no schedules yet</div>
          <div className="mono text-[10px] text-[#c7c1ff]/40 mt-1">automate novo to run at fixed times</div>
          <button onClick={() => setOpenNew(true)} className="mt-4 btn-accent text-[11px] uppercase tracking-widest mono flex items-center gap-1.5 mx-auto" data-testid="schedule-new-empty">
            <Plus size={12} /> create first schedule
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {items.map(s => (
            <div
              key={s.id}
              data-testid={`schedule-${s.id}`}
              className="panel p-4"
              style={{
                clipPath: "polygon(0 0, calc(100% - 12px) 0, 100% 12px, 100% 100%, 12px 100%, 0 calc(100% - 12px))",
                opacity: s.enabled ? 1 : 0.55,
              }}
            >
              <div className="flex items-start gap-3">
                <span
                  className="inline-flex items-center justify-center flex-shrink-0"
                  style={{
                    width: 32, height: 36,
                    clipPath: "polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)",
                    background: s.enabled ? "rgba(59,130,246,0.22)" : "rgba(168,227,239,0.12)",
                    border: `1px solid ${s.enabled ? "#3b82f6" : "#c7c1ff"}55`,
                  }}
                >
                  <ClockIcon size={13} color={s.enabled ? "#3b82f6" : "#c7c1ff"} />
                </span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <div className="font-medium text-[#3b82f6]">{s.name}</div>
                    <span className="mono text-[9px] uppercase tracking-widest text-[#a78bfa]">{cadenceLabel(s)}</span>
                  </div>
                  <div className="text-sm text-[#e8f4f8]/85 mt-1">{s.goal}</div>
                  <div className="mt-2 flex items-center gap-4 mono text-[10px] uppercase tracking-widest text-[#c7c1ff]/55">
                    <span>next: <span className="text-[#34d399]">{fmtNext(s)}</span></span>
                    <span>last: <span className="text-[#c7c1ff]">{fmtLast(s)}</span> {s.last_run_status && <span className={s.last_run_status === "done" ? "text-[#34d399]" : "text-[#f87171]"}>· {s.last_run_status}</span>}</span>
                  </div>
                </div>
                <div className="flex flex-col gap-1.5">
                  <button
                    data-testid={`schedule-run-now-${s.id}`}
                    onClick={() => runNow(s)}
                    disabled={s._runNowBusy}
                    title="Run now"
                    className="p-2 hover:bg-[#8b5cf6]/10"
                  >
                    {s._runNowBusy ? <Loader2 size={13} className="animate-spin" /> : <PlayCircle size={13} color="#34d399" />}
                  </button>
                  <button
                    data-testid={`schedule-toggle-${s.id}`}
                    onClick={() => toggle(s)}
                    title={s.enabled ? "Disable" : "Enable"}
                    className="p-2 hover:bg-[#3b82f6]/10"
                  >
                    <Power size={13} color={s.enabled ? "#34d399" : "#c7c1ff"} />
                  </button>
                  <button
                    data-testid={`schedule-delete-${s.id}`}
                    onClick={() => remove(s)}
                    title="Delete"
                    className="p-2 hover:bg-red-500/10 text-red-400"
                  >
                    <Trash2 size={13} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {openNew && <NewScheduleModal onClose={() => setOpenNew(false)} onCreated={() => { setOpenNew(false); load(); }} />}
    </div>
  );
}

function NewScheduleModal({ onClose, onCreated }) {
  const [name, setName] = useState("");
  const [goal, setGoal] = useState("");
  const [cadence, setCadence] = useState("daily");
  const [time, setTime] = useState("09:00");
  const [dayOfWeek, setDayOfWeek] = useState(0);
  const [intervalMinutes, setIntervalMinutes] = useState(60);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const tz_offset_minutes = -new Date().getTimezoneOffset();

  const submit = async (e) => {
    e?.preventDefault();
    if (!name.trim() || !goal.trim()) { setErr("Name and goal required."); return; }
    setBusy(true); setErr("");
    try {
      await api.post("/wingman/schedules", {
        name, goal, cadence, time,
        day_of_week: dayOfWeek,
        interval_minutes: intervalMinutes,
        tz_offset_minutes,
      });
      onCreated?.();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed");
    }
    setBusy(false);
  };

  const TEMPLATES = [
    { name: "Morning team prep", goal: "Check team attendance and summarize my unread emails. Create a task for any urgent email follow-ups." },
    { name: "End-of-day wrap", goal: "Summarize today's emails, list any pending approvals, and create tomorrow's task list." },
    { name: "Weekly content push", goal: "Draft a Twitter post and a LinkedIn post about our progress this week." },
  ];

  return (
    <div className="absolute inset-0 z-[50] flex items-center justify-center" style={{ background: "rgba(0,0,0,0.6)", backdropFilter: "blur(8px)" }} onClick={onClose}>
      <div
        onClick={(e) => e.stopPropagation()}
        className="panel-strong w-[560px] max-w-[94%] max-h-[88vh] overflow-y-auto p-5"
        style={{ clipPath: "polygon(0 0, calc(100% - 16px) 0, 100% 16px, 100% 100%, 16px 100%, 0 calc(100% - 16px))" }}
        data-testid="new-schedule-modal"
      >
        <div className="flex items-center gap-2 mb-4">
          <Calendar size={14} color="#3b82f6" />
          <div className="mono text-[11px] uppercase tracking-[0.3em] text-[#3b82f6]">// new novo schedule</div>
          <button onClick={onClose} className="ml-auto text-[#c7c1ff]/60 hover:text-[#3b82f6]"><X size={16} /></button>
        </div>

        <form onSubmit={submit} className="space-y-3">
          <div>
            <div className="mono text-[9px] uppercase tracking-widest text-[#c7c1ff]/55 mb-1">name</div>
            <input
              data-testid="schedule-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Morning team prep"
              className="w-full bg-[#0e1a22] border border-[#8b5cf6]/25 px-3 py-2 text-sm outline-none focus:border-[#8b5cf6]/60 mono"
              autoFocus
            />
          </div>

          <div>
            <div className="mono text-[9px] uppercase tracking-widest text-[#c7c1ff]/55 mb-1">goal · what should novo do?</div>
            <textarea
              data-testid="schedule-goal"
              value={goal}
              onChange={(e) => setGoal(e.target.value)}
              placeholder="e.g. Check team attendance and summarize unread emails"
              rows={3}
              className="w-full bg-[#0e1a22] border border-[#8b5cf6]/25 px-3 py-2 text-sm outline-none focus:border-[#8b5cf6]/60"
            />
            <div className="flex gap-2 mt-2 flex-wrap">
              {TEMPLATES.map((t, i) => (
                <button
                  key={i}
                  type="button"
                  data-testid={`schedule-template-${i}`}
                  onClick={() => { setName(t.name); setGoal(t.goal); }}
                  className="text-[10px] mono uppercase tracking-widest px-2 py-1 border border-[#8b5cf6]/20 hover:border-[#3b82f6]/50 hover:text-[#3b82f6] text-[#c7c1ff]/65"
                >
                  {t.name}
                </button>
              ))}
            </div>
          </div>

          <div>
            <div className="mono text-[9px] uppercase tracking-widest text-[#c7c1ff]/55 mb-1">cadence</div>
            <div className="grid grid-cols-3 gap-2">
              {[
                { id: "daily", label: "Daily" },
                { id: "weekly", label: "Weekly" },
                { id: "interval", label: "Every N min" },
              ].map(c => (
                <button
                  key={c.id}
                  type="button"
                  data-testid={`schedule-cadence-${c.id}`}
                  onClick={() => setCadence(c.id)}
                  className="px-3 py-2 mono text-[10px] uppercase tracking-widest"
                  style={{
                    background: cadence === c.id ? "rgba(59,130,246,0.18)" : "transparent",
                    border: `1px solid ${cadence === c.id ? "#3b82f6" : "rgba(139,92,246,0.18)"}55`,
                    color: cadence === c.id ? "#3b82f6" : "#c7c1ff",
                  }}
                >{c.label}</button>
              ))}
            </div>
          </div>

          {cadence === "daily" && (
            <div>
              <div className="mono text-[9px] uppercase tracking-widest text-[#c7c1ff]/55 mb-1">run at (local time)</div>
              <input
                data-testid="schedule-time"
                type="time"
                value={time}
                onChange={(e) => setTime(e.target.value)}
                className="bg-[#0e1a22] border border-[#8b5cf6]/25 px-3 py-2 text-sm outline-none focus:border-[#8b5cf6]/60 mono"
              />
            </div>
          )}

          {cadence === "weekly" && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <div className="mono text-[9px] uppercase tracking-widest text-[#c7c1ff]/55 mb-1">day</div>
                <select
                  data-testid="schedule-dow"
                  value={dayOfWeek}
                  onChange={(e) => setDayOfWeek(Number(e.target.value))}
                  className="w-full bg-[#0e1a22] border border-[#8b5cf6]/25 px-3 py-2 text-sm outline-none focus:border-[#8b5cf6]/60 mono"
                >
                  {DAYS.map((d, i) => <option key={i} value={i}>{d}</option>)}
                </select>
              </div>
              <div>
                <div className="mono text-[9px] uppercase tracking-widest text-[#c7c1ff]/55 mb-1">run at</div>
                <input
                  data-testid="schedule-time"
                  type="time"
                  value={time}
                  onChange={(e) => setTime(e.target.value)}
                  className="w-full bg-[#0e1a22] border border-[#8b5cf6]/25 px-3 py-2 text-sm outline-none focus:border-[#8b5cf6]/60 mono"
                />
              </div>
            </div>
          )}

          {cadence === "interval" && (
            <div>
              <div className="mono text-[9px] uppercase tracking-widest text-[#c7c1ff]/55 mb-1">every (minutes)</div>
              <input
                data-testid="schedule-interval"
                type="number"
                min={1}
                value={intervalMinutes}
                onChange={(e) => setIntervalMinutes(Number(e.target.value))}
                className="bg-[#0e1a22] border border-[#8b5cf6]/25 px-3 py-2 text-sm outline-none focus:border-[#8b5cf6]/60 mono"
              />
            </div>
          )}

          <div className="mono text-[9px] text-[#c7c1ff]/45 uppercase tracking-widest">
            // timezone: utc{tz_offset_minutes >= 0 ? "+" : ""}{(tz_offset_minutes / 60).toFixed(1)}h · push notification on completion
          </div>

          {err && <div className="text-xs text-[#f87171] mono">{err}</div>}

          <div className="flex justify-end gap-2 pt-1">
            <button type="button" onClick={onClose} className="btn-ghost text-[11px] uppercase tracking-widest mono">cancel</button>
            <button
              data-testid="schedule-create"
              disabled={busy}
              type="submit"
              className="btn-accent text-[11px] uppercase tracking-widest mono flex items-center gap-1.5"
            >
              {busy ? <Loader2 size={12} className="animate-spin" /> : <Calendar size={12} />}
              create schedule
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
